import type { Session } from '@supabase/supabase-js'
import type { Profile } from './profile'

/**
 * The six screens a user can be sent to, before any tab of the app proper is reachable.
 *
 * - `SIGNED_OUT` — no session; show the sign-in screen.
 * - `NO_PROFILE` — a live session with no `profiles` row. See `decideGate` below: this is the
 *   state that loops forever if it is missed.
 * - `PENDING` — signed in, profile exists, invite not yet redeemed; show the join/invite screen.
 * - `DISABLED` — an admin has disabled the account; show the disabled notice.
 * - `SETUP` — active but the profile is missing something the app needs to function.
 * - `READY` — active and complete; the app proper.
 */
export type GateState = 'SIGNED_OUT' | 'NO_PROFILE' | 'PENDING' | 'DISABLED' | 'SETUP' | 'READY'

/**
 * Decides which screen a user should see, from nothing but their session and profile.
 *
 * A pure function by design: no DOM, no network, no Supabase client — only the `Session` and
 * `Profile` *types*. `SessionProvider` owns *when* to call this (once loading has resolved);
 * this function has no notion of loading and must never grow one, because "still loading" is
 * not a screen and a `loading` branch here would let a transient null masquerade as a verdict.
 *
 * **The order of the branches below is the security property, not an implementation detail.**
 * Each check short-circuits the rest:
 *
 * 1. No session wins over everything, including a non-null `profile` argument. A caller should
 *    never pass a profile without a session, but if one somehow does, session absence is the one
 *    fact nothing else can override.
 * 2. `profile === null` with a live session is `NO_PROFILE` — reached when `handle_new_user`
 *    failed, the fetch errored, or RLS legitimately returned zero rows. It must never fall
 *    through to `SIGNED_OUT`: that bounces a signed-in user to `/sign-in`, where they sign in
 *    successfully and land on the identical verdict, forever, with nothing on screen explaining
 *    why. It is also necessarily checked before any `profile.status` read — there is no profile
 *    to read a status off.
 * 3. `DISABLED` beats `PENDING` and beats completeness: a disabled account shows `DISABLED` even
 *    when its profile is fully filled out. Disabling always wins.
 * 4. `PENDING` beats completeness, for the same reason: a PENDING user whose fields happen to be
 *    filled in (set up before an admin reset their status, or a redemption that raced) must still
 *    see `PENDING`, never `SETUP` or `READY`. Only redemption — a database-side transaction —
 *    moves a user out of PENDING; this function never does.
 * 5/6. `ACTIVE` splits on `isProfileComplete`: `SETUP` when something's missing, else `READY`.
 *
 * @param session - The current Supabase session, or `null` when signed out.
 * @param profile - The signed-in user's own `profiles` row, or `null` when there is none.
 * @returns The single `GateState` this pair resolves to.
 */
export function decideGate(session: Session | null, profile: Profile | null): GateState {
  if (session === null) return 'SIGNED_OUT'
  if (profile === null) return 'NO_PROFILE'
  if (profile.status === 'DISABLED') return 'DISABLED'
  if (profile.status === 'PENDING') return 'PENDING'
  return isProfileComplete(profile) ? 'READY' : 'SETUP'
}

/**
 * Whether a profile holds enough for the app to function — not whether every field is filled.
 *
 * `classification`, `station` and `facebook_username` are recorded but, per their own column
 * comments in `0001_profiles.sql`, never enforced by any other logic; they do not gate `READY`.
 * The fields that do:
 *
 * - `display_name` — always required. An empty string counts as missing (`''` is falsy), which is
 *   the behaviour we want and needs no special-casing.
 * - `roster_type` — always required; without it there is no roster to derive at all.
 * - `block_code` — required only for `'BLOCK'` and `'BLOCK_PARTIAL'`, the roster types that derive
 *   shifts from a block. `'FWA'` derives nothing from a block, so a null `block_code` on an FWA
 *   profile is not incompleteness, and checking it unconditionally would strand every FWA user on
 *   `SETUP` with nothing to fill in.
 *
 * Exported for reuse by `SetupScreen`, so the screen and the gate cannot disagree about what
 * "complete" means.
 *
 * @param profile - The profile to check.
 * @returns `true` when the profile is complete enough for `READY`.
 */
export function isProfileComplete(profile: Profile): boolean {
  if (!profile.display_name) return false
  if (!profile.roster_type) return false
  if (
    (profile.roster_type === 'BLOCK' || profile.roster_type === 'BLOCK_PARTIAL') &&
    !profile.block_code
  ) {
    return false
  }
  return true
}
